"use client"

import { useMemo, useState, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"
import { readNonNegative } from "@/lib/tools/numeric-input"

// ---------- Decision logic ----------
// Source of truth: docs/Page-Types-And-Templates.md (Lifespan / Replacement Decision Rules).
// Hard rules:
//  • Rule 1 — age >= 10 ⇒ replace (ENERGY STAR 10-year energy cutoff)
//  • Rule 4 — repair quote >= 50% of new price ⇒ replace (the 50%-rule)
//  • Rule 2 — base lifespan by fridge type
//  • All thresholds preserved verbatim from the legacy calculator.

type Tone = "healthy" | "watch" | "replace"

interface FridgeTypeData {
  avgNewPrice: number
  avgLifespan: number
}

const FRIDGE_TYPES: Record<string, FridgeTypeData> = {
  "Top Freezer": { avgNewPrice: 800, avgLifespan: 14 },
  "Bottom Freezer": { avgNewPrice: 1200, avgLifespan: 13 },
  "Side-by-Side": { avgNewPrice: 1500, avgLifespan: 12 },
  "French Door": { avgNewPrice: 2200, avgLifespan: 12 },
  "Mini Fridge": { avgNewPrice: 300, avgLifespan: 10 },
  "Built-In": { avgNewPrice: 5000, avgLifespan: 15 },
  Commercial: { avgNewPrice: 4000, avgLifespan: 15 },
}

interface Verdict {
  tone: Tone
  headline: string
  subline: string
  reasons: string[]
  costRatio: number
  remainingLife: number
  newUnitCost: number
  repairCost: number
}

function computeVerdict(fridgeType: string, ageNum: number, quoteNum: number): Verdict | null {
  const typeData = FRIDGE_TYPES[fridgeType]
  if (typeData === undefined) return null

  const costRatio = quoteNum / typeData.avgNewPrice
  const remainingLife = Math.max(0, typeData.avgLifespan - ageNum)

  // Rule 1 — hard 10-year cutoff (ENERGY STAR rule)
  const tooOld = ageNum >= 10
  // Rule 4 — 50%-rule
  const tooExpensive = costRatio > 0.5
  const nearEndOfLife = remainingLife <= 2

  let tone: Tone
  let headline: string
  let subline: string
  let reasons: string[]

  if (tooExpensive && tooOld) {
    tone = "replace"
    headline = "Replace it."
    subline = `Repair runs $${quoteNum.toLocaleString()} — ${Math.round(costRatio * 100)}% of a new ${fridgeType}. At ${ageNum} years, you're past the 10-year ENERGY STAR replacement cutoff.`
    reasons = [
      `Repair cost is ${Math.round(costRatio * 100)}% of a new unit ($${typeData.avgNewPrice.toLocaleString()}) — exceeds the 50% repair rule.`,
      `Age ${ageNum} years exceeds the ENERGY STAR 10-year energy-payback cutoff.`,
      `Estimated remaining lifespan: ~${remainingLife} year${remainingLife !== 1 ? "s" : ""}.`,
    ]
  } else if (tooExpensive) {
    tone = "replace"
    headline = "Replace it."
    subline = `Repair quote is $${quoteNum.toLocaleString()} — ${Math.round(costRatio * 100)}% of a new unit. The 50%-rule says replace once a repair crosses half the replacement cost.`
    reasons = [
      `Repair cost is ${Math.round(costRatio * 100)}% of a new unit ($${typeData.avgNewPrice.toLocaleString()}) — exceeds the 50% repair rule.`,
      `Additional repairs are likely once one major component fails on a ${ageNum}-year-old unit.`,
    ]
  } else if (tooOld && nearEndOfLife) {
    tone = "replace"
    headline = "Replace it."
    subline = `${ageNum} years old with only ~${remainingLife} year${remainingLife !== 1 ? "s" : ""} of expected life left. ENERGY STAR puts payback on a new unit at 2–4 years on energy savings alone.`
    reasons = [
      `Age ${ageNum} years exceeds the 10-year ENERGY STAR cutoff.`,
      `Only ~${remainingLife} year${remainingLife !== 1 ? "s" : ""} of expected lifespan remaining.`,
      `Repair cost ($${quoteNum.toLocaleString()}) is ${Math.round(costRatio * 100)}% of new — borderline by the 50% rule.`,
    ]
  } else if (tooOld) {
    tone = "watch"
    headline = "Plan a replacement."
    subline = `Repair price is reasonable, but at ${ageNum} years you're past the ENERGY STAR 10-year cutoff. Pay the repair if you need it tonight, but budget for a replacement in the next 12–18 months.`
    reasons = [
      `Age ${ageNum} years exceeds the 10-year ENERGY STAR cutoff.`,
      `Repair quote ($${quoteNum.toLocaleString()}) is ${Math.round(costRatio * 100)}% of new — under the 50% rule.`,
      `Newer ENERGY STAR models use up to 35% less energy.`,
    ]
  } else {
    tone = "healthy"
    headline = "Repair it."
    subline = `Repair runs $${quoteNum.toLocaleString()} — only ${Math.round(costRatio * 100)}% of a new ${fridgeType}. You still have ~${remainingLife} year${remainingLife !== 1 ? "s" : ""} of expected service.`
    reasons = [
      `Repair cost is ${Math.round(costRatio * 100)}% of a new unit ($${typeData.avgNewPrice.toLocaleString()}) — well under the 50% repair rule.`,
      `Age ${ageNum} years is under the 10-year ENERGY STAR cutoff.`,
      `~${remainingLife} year${remainingLife !== 1 ? "s" : ""} of expected lifespan remaining.`,
    ]
  }

  return {
    tone,
    headline,
    subline,
    reasons,
    costRatio,
    remainingLife,
    newUnitCost: typeData.avgNewPrice,
    repairCost: quoteNum,
  }
}

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Verdict · Repair",
  watch: "Verdict · Plan ahead",
  replace: "Verdict · Replace",
}
const TONE_CTA: Record<Tone, { label: string; href: string }> = {
  healthy: { label: "See maintenance tips →", href: "/tools/refrigerator-temperature-settings-guide" },
  watch: { label: "Browse 2026 models →", href: "/collections/refrigerators" },
  replace: { label: "Shop replacements →", href: "/collections/refrigerators" },
}

// ---------- Page ----------

const META = {
  name: "Repair or Replace Refrigerator",
  faqs: [
    {
      question: "What's the 50% rule?",
      answer:
        "If a repair quote exceeds 50% of the cost of a comparable new unit, replace. This is industry-standard guidance from major appliance retailers — paying half the price of new for one repair on a 10-year-old machine rarely pays back.",
    },
    {
      question: "Why is age 10 the cutoff?",
      answer:
        "Per ENERGY STAR, refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Even if your unit still runs, a new one typically pays back in 2–4 years on electricity savings alone.",
    },
    {
      question: "What if the repair tech says it's worth fixing?",
      answer:
        "Service techs are paid to repair, not advise on replacement. Ask them to also quote a comparable new unit so you can compare. If the repair plus expected next-failure cost approaches the new-unit price, replace.",
    },
    {
      question: "Does it matter what kind of repair it is?",
      answer:
        "Yes. Compressor, sealed-system, and main control board failures rarely make sense to repair on a 10+ year-old fridge — the rest of the sealed system is aging in parallel. Door gasket, water valve, ice maker, and thermistor swaps are usually worth doing.",
    },
    {
      question: "How accurate is the 'average new price' for my fridge type?",
      answer:
        "We use category midpoints — Top Freezer ~$800, Bottom Freezer ~$1,200, Side-by-Side ~$1,500, French Door ~$2,200, Built-In ~$5,000. If you're shopping a specific model, plug its actual price into the 50% rule manually.",
    },
  ],
}

const RELATED = [
  {
    title: "Refrigerator Lifespan Calculator",
    category: "Specialty",
    description: "Estimate years of life remaining.",
    href: "/tools/refrigerator-lifespan-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Age Calculator",
    category: "Specialty",
    description: "Decode the serial number to find the build year.",
    href: "/tools/refrigerator-age-calculator",
    time: "~30 sec",
  },
  {
    title: "Energy Cost Calculator",
    category: "Energy & Cost",
    description: "Annual operating cost for any fridge.",
    href: "/tools/energy-cost-calculator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Size Calculator",
    category: "Size & Capacity",
    description: "Size a replacement properly.",
    href: "/tools/refrigerator-size-calculator",
    time: "~45 sec",
  },
]

function InputLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
        {children}
      </label>
      {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
    </div>
  )
}

export function RepairOrReplaceRefrigeratorClient(_props?: { toolData?: ToolComponentData }) {
  const [fridgeType, setFridgeType] = useState<string>("French Door")
  const [age, setAge] = useState<string>("11")
  const [repairQuote, setRepairQuote] = useState<string>("450")

  const verdict = useMemo(() => {
    // Age 0 is a real reading — a fridge bought this year — so zero is admitted.
    // The $20,000 quote ceiling is 4× the priciest category's new-unit price,
    // past which the 50%-rule answer is meaningless. FRIDGE-384.
    const ageNum = readNonNegative(age, { min: 0, max: 30 })
    const quoteNum = readNonNegative(repairQuote, { min: 0, max: 20000 })
    if (fridgeType === "" || ageNum === null || quoteNum === null) return null
    return computeVerdict(fridgeType, ageNum, quoteNum)
  }, [fridgeType, age, repairQuote])

  return (
    <ToolPageShell
      toolName="Repair or Replace Refrigerator"
      heroEyebrow="Specialty"
      heroTitlePre="Should you repair"
      heroTitleItalic="or replace"
      heroTitlePost="your fridge?"
      heroSubtitle="Three numbers, one direct answer. We apply the 50% repair rule and the ENERGY STAR 10-year energy cutoff — no upsell, no hand-waving."
      heroMeta={["⏱ ~30 sec", "ENERGY STAR + 50% repair rule"]}
      heroShare={<ToolShareBar title="Should you repair or replace your refrigerator?" />}
      faqs={META.faqs}
      relatedTools={RELATED}
      cta={{
        title: "Explore replacement refrigerators.",
        body: "Answer five questions to explore a starting product group, with prices where available. Verify dimensions, features and warranty terms on each product page.",
        buttonLabel: "Find your fridge",
        buttonHref: "/tools/find-your-fridge",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Tell us about your fridge
            </div>

            <div className="space-y-6">
              <div>
                <InputLabel hint="Sets the average new-unit price we compare against">
                  Refrigerator type
                </InputLabel>
                <select
                  value={fridgeType}
                  onChange={(e) => setFridgeType(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                >
                  {Object.entries(FRIDGE_TYPES).map(([t, d]) => (
                    <option key={t} value={t}>
                      {t} · avg new {`$${d.avgNewPrice.toLocaleString()}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <InputLabel hint="Years since manufacture — check the serial number if unsure">
                  Age (years)
                </InputLabel>
                <input
                  type="number"
                  min={0}
                  max={30}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>

              <div>
                <InputLabel hint="Total quoted repair cost (parts + labor)">
                  Repair quote ($)
                </InputLabel>
                <input
                  type="number"
                  min={0}
                  max={20000}
                  step={10}
                  value={repairQuote}
                  onChange={(e) => setRepairQuote(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
            </div>
          </div>

          {/* Verdict */}
          <div>
            {verdict && (
              <>
                <div
                  className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
                  style={{
                    background: TONE_BG[verdict.tone],
                    border: `1px solid ${TONE_BORDER[verdict.tone]}30`,
                  }}
                >
                  <div
                    className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                    style={{ color: TONE_BORDER[verdict.tone] }}
                  >
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: TONE_BORDER[verdict.tone] }}
                    />
                    {TONE_LABEL[verdict.tone]}
                  </div>
                  <div
                    className="text-gray-900 font-black"
                    style={{
                      fontSize: "clamp(44px, 7vw, 80px)",
                      letterSpacing: "-0.05em",
                      lineHeight: 0.95,
                      textWrap: "balance" as never,
                    }}
                  >
                    {verdict.headline}
                  </div>
                  <p
                    className="mt-5 md:mt-6 text-gray-700"
                    style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
                  >
                    {verdict.subline}
                  </p>
                </div>

                <div className="grid grid-cols-3 border-y border-gray-300 divide-x divide-gray-300 mb-6 md:mb-8">
                  <div className="py-5 pr-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Repair / new
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 30,
                        letterSpacing: "-0.03em",
                        color: verdict.costRatio > 0.5 ? TONE_BORDER[verdict.tone] : "#111827",
                      }}
                    >
                      {Math.round(verdict.costRatio * 100)}
                      <span className="text-[13px] font-bold text-gray-500 ml-0.5">%</span>
                    </div>
                  </div>
                  <div className="py-5 px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      New unit
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 30, letterSpacing: "-0.03em" }}
                    >
                      {`$${verdict.newUnitCost.toLocaleString()}`}
                    </div>
                  </div>
                  <div className="py-5 pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Years left
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 30,
                        letterSpacing: "-0.03em",
                        color: TONE_BORDER[verdict.tone],
                      }}
                    >
                      {verdict.remainingLife}
                      <span className="text-[13px] font-bold text-gray-500 ml-1">yrs</span>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                    Why this verdict
                  </div>
                  <ul className="space-y-2">
                    {verdict.reasons.map((r) => (
                      <li
                        key={r}
                        className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200"
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                          style={{ background: TONE_BORDER[verdict.tone] }}
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                        <p className="text-[14px] text-gray-700 leading-[1.55]">{r}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={TONE_CTA[verdict.tone].href}
                  className="block h-14 rounded-lg text-[15px] font-extrabold tracking-tight text-white inline-flex items-center justify-center w-full"
                  style={{
                    background: verdict.tone === "replace" ? "#ff9951" : "#111827",
                    boxShadow:
                      verdict.tone === "replace" ? "0 8px 22px rgba(255,153,81,0.35)" : undefined,
                  }}
                >
                  {TONE_CTA[verdict.tone].label}
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If it's time"
          title="French door replacements"
          description="Wider shelves, bottom-freezer access, highest resale value."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "The 50% repair rule",
                d: "If a repair quote exceeds 50% of the cost of a comparable new unit, replace. Industry-standard guidance from major appliance retailers.",
              },
              {
                n: "02",
                t: "ENERGY STAR 10-year cutoff",
                d: "Refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Replacement payback on energy alone is typically 2–4 years.",
              },
              {
                n: "03",
                t: "Average new-unit prices",
                d: "Category midpoints — Top Freezer $800, Bottom Freezer $1,200, Side-by-Side $1,500, French Door $2,200, Built-In $5,000. AHAM industry data.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">
                  {m.t}
                </h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer alternatives"
          description="Same back-saving freezer drawer at a lower price than French door."
          collectionType="bottom-freezer"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
